import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, Divider, Snackbar, Stack, Typography,
} from '@mui/material';
import { RestoreFromTrash as RestoreIcon } from '@mui/icons-material';
import DocumentService, { DeletedDocument } from '../../api/services/document.service';
import { FileGlyph, GeneratedName } from '../../components/files/vaultUI';
import FilesShell from '../../components/files/FilesShell';
import PageHeader from '../../components/ui/PageHeader';
import { LoadingState } from '../../components/ui/States';

// Deleting a compliance document is the kind of mistake you discover weeks
// later, so nothing is really gone for 30 days. The countdown is the point
// of the screen — a list without it is just a list.

const RecentlyDeletedPage: React.FC = () => {
  const [items, setItems] = useState<DeletedDocument[]>([]);
  const [windowDays, setWindowDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restored, setRestored] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await DocumentService.getRecentlyDeleted();
      setItems(result.items || []);
      setWindowDays(result.windowDays || 30);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load recently deleted files');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const restore = async (documentId: string) => {
    setBusyId(documentId);
    try {
      const result = await DocumentService.restore(documentId);
      setRestored(result.fileName);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not restore this document');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <FilesShell
      header={
        <PageHeader
          title="Recently deleted"
          subtitle={`Recoverable for ${windowDays} days. After that they are purged for good.`}
        />
      }
    >

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <LoadingState label="Loading deleted documents…" />
      ) : (
        <Card sx={{ overflow: 'hidden' }}>
          {items.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">Nothing has been deleted recently.</Typography>
            </Box>
          ) : (
            items.map((item, index) => (
              <React.Fragment key={item._id}>
                {index > 0 && <Divider />}
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Stack direction="row" alignItems="center" gap={2}>
                    <FileGlyph contentType={item.file?.contentType} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <GeneratedName name={item.fileName} />
                      <Typography variant="caption" color="text.secondary" display="block">
                        {item.ownerLabel || 'Company'} ·{' '}
                        <Box
                          component="span"
                          sx={{ color: item.daysLeft <= 3 ? 'error.main' : 'text.secondary', fontWeight: item.daysLeft <= 3 ? 700 : 400 }}
                        >
                          {item.daysLeft <= 0
                            ? 'Purges today'
                            : `${item.daysLeft} ${item.daysLeft === 1 ? 'day' : 'days'} left`}
                        </Box>
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      startIcon={<RestoreIcon />}
                      onClick={() => restore(item._id)}
                      disabled={busyId === item._id}
                    >
                      {busyId === item._id ? 'Restoring…' : 'Restore'}
                    </Button>
                  </Stack>
                </Box>
              </React.Fragment>
            ))
          )}
        </Card>
      )}

      <Snackbar
        open={!!restored}
        autoHideDuration={4000}
        onClose={() => setRestored(null)}
        message={restored ? `Restored ${restored}` : ''}
      />
    </FilesShell>
  );
};

export default RecentlyDeletedPage;
